import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, useRef, useState } from "react";
import { LuChevronDown, LuSearch } from "react-icons/lu";

import { Popover, PopoverPosition } from "../Popover/Popover";
import { InputSize } from "./Input";

export interface SelectPrefixOption {
  icon?: ReactNode;
  label: string;
  value: string;
  searchTerms?: string; // additional terms for search
}

interface InputSelectPrefixProps {
  children: ReactNode;
  inputSize?: InputSize;
  error?: boolean;
  options: SelectPrefixOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  searchable?: boolean;
  disabled?: boolean;
}

export function InputSelectPrefix({
  children,
  inputSize = InputSize.MEDIUM,
  error,
  options,
  selectedValue,
  onChange,
  onOpenChange,
  searchable = true,
  disabled = false
}: InputSelectPrefixProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const setOpenState = (value: boolean) => {
    setIsOpen(value);
    onOpenChange?.(value);
  };

  const sizes = {
    [InputSize.SMALL]: "h-[26px] px-2 text-sm",
    [InputSize.MEDIUM]: "h-[34px] px-2"
  };

  const selectedOption = options.find((opt) => opt.value === selectedValue);

  const filteredOptions = searchable
    ? options.filter((opt) => {
        const query = searchQuery.toLowerCase();
        return opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query) || opt.searchTerms?.toLowerCase().includes(query);
      })
    : options;

  const handleOpen = () => {
    if (!disabled) {
      setOpenState(true);
      // focus search input after popover opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleSelect = (value: string) => {
    onChange(value);
    setOpenState(false);
    setSearchQuery("");
  };

  const triggerButton = (
    <button
      type="button"
      onClick={handleOpen}
      disabled={disabled}
      data-error={error || undefined}
      data-open={isOpen || undefined}
      className={filterClasses(
        "group flex cursor-pointer items-center gap-1 rounded-l-sm border-y border-r border-l border-r-transparent bg-linear-to-b from-white to-grey-50 transition-all duration-150 ease-in-out",
        "border-grey-400 hover:z-10 hover:border-orange-500",
        "data-error:border-red-500 data-error:hover:border-red-500",
        "data-open:z-10 data-open:border-r data-open:border-orange-500 data-open:ring-2 data-open:ring-orange-100",
        "disabled:cursor-not-allowed disabled:border-grey-400 disabled:bg-grey-200",
        sizes[inputSize]
      )}
    >
      {selectedOption?.icon && <span className="shrink-0 group-disabled:text-grey-500">{selectedOption.icon}</span>}
      <span className="body-sm shrink-0 text-black group-disabled:text-grey-500">{selectedOption?.value}</span>
      <LuChevronDown
        className={filterClasses(
          "h-4 w-4 shrink-0 text-grey-600 transition-transform duration-200 ease-out group-disabled:text-grey-500",
          "group-data-open:rotate-180"
        )}
      />
    </button>
  );

  return (
    <div className="flex items-stretch">
      <Popover
        isOpen={isOpen}
        position={PopoverPosition.LEFT}
        onEscapeKeyDown={() => {
          setOpenState(false);
          setSearchQuery("");
        }}
        onInteractOutside={() => {
          setOpenState(false);
          setSearchQuery("");
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        trigger={triggerButton}
        className="min-w-[200px]"
      >
        {searchable && (
          <div className="mb-2 border-b border-grey-300">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={filterClasses(
                  "w-full rounded-sm border border-grey-500 bg-white pr-9 pl-3 text-grey-900 transition-all outline-none",
                  "placeholder:text-grey-600",
                  "hover:border-orange-500",
                  "focus:border-orange-500 focus:ring-2 focus:ring-orange-100",
                  "h-8 text-sm"
                )}
              />
              <LuSearch className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-grey-600" />
            </div>
          </div>
        )}

        <div className="max-h-60 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-grey-600">No results found</div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={`${option.label}-${option.value}`}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={filterClasses(
                  "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left transition-colors",
                  "hover:bg-grey-50",
                  selectedValue === option.value && "text-orange-600"
                )}
              >
                {option.icon && <span className="shrink-0">{option.icon}</span>}
                <span className="body-sm flex-1">{option.label}</span>
                <span className="body-mono text-black">{option.value}</span>
              </button>
            ))
          )}
        </div>
      </Popover>
      {children}
    </div>
  );
}
